import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';

interface MultiSelectOption {
  id: string;
  label: string;
  value: string;
  count?: number;
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  maxHeight?: string;
  showClearButton?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Seçiniz...",
  className = "",
  disabled = false,
  loading = false,
  maxHeight = "200px",
  showClearButton = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle option toggle
  const handleOptionToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedValues.length === filteredOptions.length) {
      onChange([]);
    } else {
      const allValues = filteredOptions.map(option => option.value);
      onChange(allValues);
    }
  };

  // Handle clear all
  const handleClearAll = () => {
    onChange([]);
  };

  // Get selected options for display
  const selectedOptions = options.filter(option => 
    selectedValues.includes(option.value)
  );

  const isAllSelected = filteredOptions.length > 0 && 
    filteredOptions.every(option => selectedValues.includes(option.value));

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className={`
          w-full px-3 py-2 text-left border border-gray-300 rounded-md shadow-sm
          focus:outline-none focus:ring-2 focus:ring-[#9B51E0] focus:border-transparent
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'}
          ${loading ? 'bg-gray-50' : ''}
        `}
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-1 min-h-[20px]">
            {selectedOptions.length === 0 ? (
              <span className="text-gray-500">{placeholder}</span>
            ) : (
              selectedOptions.map(option => (
                <span
                  key={option.value}
                  className="inline-flex items-center px-2 py-1 text-xs bg-[#9B51E0] text-white rounded-md mr-1 mb-1"
                >
                  {option.label}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(selectedValues.filter(v => v !== option.value));
                    }}
                    className="ml-1 hover:bg-[#8B41D0] rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))
            )}
          </div>
          <ChevronDown 
            className={`w-4 h-4 text-gray-400 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`} 
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Sınıf ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#9B51E0]"
              autoFocus
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between p-2 border-b border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs text-[#9B51E0] hover:text-[#8B41D0] font-medium"
            >
              {isAllSelected ? 'Tümünü Kaldır' : 'Tümünü Seç'}
            </button>
            {selectedValues.length > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs text-red-600 hover:text-red-700 font-medium"
              >
                Temizle ({selectedValues.length})
              </button>
            )}
          </div>

          {/* Options List */}
          <div 
            className="max-h-[200px] overflow-y-auto"
            style={{ maxHeight }}
          >
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                {searchTerm ? 'Arama kriterlerine uygun sınıf bulunamadı' : 'Sınıf bulunamadı'}
              </div>
            ) : (
              filteredOptions.map(option => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleOptionToggle(option.value)}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between text-gray-900"
                  >
                    <div className="flex items-center">
                      <span className="font-medium">{option.label}</span>
                      {option.count !== undefined && (
                        <span className="ml-2 text-xs text-gray-500">
                          ({option.count})
                        </span>
                      )}
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 text-[#9B51E0]" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
            <span>
              {selectedValues.length > 0 ? `${selectedValues.length} sınıf seçildi` : 'Sınıf seçin'}
            </span>
            {showClearButton && (
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                }}
                className="text-xs text-gray-600 hover:text-gray-700 font-medium"
              >
                Kapat
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
